import { type FC, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { format } from 'date-fns'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { PageHeader, ModalDrawer, Button, ButtonVariant, ActionTile, Tag } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { useAuthsvsPasskeys } from '@egw/api'
import { Logger } from '@egw/logger'
import { registerPasskey } from '@egw/auth'
import { sendEvent } from '@egw/events'
import { v4 as uuidv4 } from 'uuid'
import { useNavigate } from 'react-router'
import { AddIcon } from '../../components'
import { type PasskeyTransport } from '../../models/passkey/passkey'
import { getAaguidData, getAaguidIcon } from '../../utils/aaguid'
import { PasskeyIcon } from '../../components/passkey-icon/passkey-icon'

const logger = new Logger('PasskeyPage')

export const PasskeyPage: FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation('features-auth')
  const navigate = useNavigate()
  const { data, isLoading, isError, refetch } = useAuthsvsPasskeys()

  const addPasskeyHandler = async (): Promise<void> => {
    try {
      await registerPasskey()
      await refetch()
    } catch (error) {
      logger.error('Error while adding passkey', error)
    } finally {
      setIsOpen(false)
    }
  }

  const handleClick = (alias: string) => {
    void navigate(`${WEALTH_ROUTES.SETTINGS_PASSKEY_DETAILS.path}?alias=${alias.toString()}`)
  }

  return (
    <>
      <PageHeader
        title={t('passkey.title')}
        subtitle={t('passkey.settings')}
        backLinkUrl={WEALTH_ROUTES.SETTINGS_PASSKEY.backPath}
      />

      <div className="egw-container pt-0">
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={5} height="80px" />
          </SkeletonTheme>
        ) : null}

        {isError ? (
          <div className="egw-container">
            <InformationCircleIcon className="w-12 h-12 fill-red-500 mb-4" />
            <p className="text-error text-2xl font-light mb-10">{t('passkey.error_title')}</p>
            <p className="text-body text-xl">{t('passkey.error_message')}</p>
          </div>
        ) : null}

        {data ? (
          <>
            <div className="flex flex-col gap-sm">
              {data.map((passkey) => {
                const formattedDate = format(new Date(passkey.updatedAt), t('date.long')) // TODO: Move to utils
                const formattedCreatedDate = format(new Date(passkey.createdAt), t('date.kebab'))
                const aaguidData = getAaguidData(passkey.aaguid)
                return (
                  <ActionTile
                    key={passkey.id}
                    image={aaguidData?.icon_light}
                    icon={!aaguidData ? <PasskeyIcon className="w-6 h-6 flex-shrink-0 self-start mr-xs" /> : null}
                    title={aaguidData?.name ?? passkey.nickName ?? t('passkey.title')}
                    description={
                      <div className="text-text-meta typo-primary-base-light flex flex-col">
                        <div className="typo-primary-base-light text-text-meta my-3xs">
                          {t('passkey.created_date', { formattedCreatedDate })}
                          {passkey.browserName && passkey.deviceOs ? (
                            <span>
                              &nbsp;
                              {t('passkey.with')} {passkey.browserName} {t('passkey.on')} {passkey.deviceOs}
                            </span>
                          ) : (
                            ''
                          )}
                        </div>
                        <div className="typo-primary-base-light text-text-meta">
                          {t('passkey.last_used_date', { formattedDate })}
                        </div>
                        {passkey.transports && passkey.transports.length > 0 ? (
                          <div className="flex items-center gap-3xs mt-xs">
                            {passkey.transports.map((transport) => (
                              <Tag key={transport} icon={getAaguidIcon(transport as PasskeyTransport)}>
                                <span className="text-olive-400 typo-primary-xs-semibold uppercase">{transport}</span>
                              </Tag>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    }
                    onClick={() => {
                      handleClick(passkey.id)
                    }}
                  />
                )
              })}
            </div>

            <Button
              variant={ButtonVariant.Secondary}
              onClick={() => {
                setIsOpen(true)
              }}
              className="my-xl"
            >
              {t('passkey.add_passkey')}
            </Button>
          </>
        ) : null}
      </div>

      <ModalDrawer
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false)
        }}
        title={t('passkey.add_passkey')}
      >
        <AddIcon />
        <div className="border-light flex flex-col border-b py-4 mt-2">
          <p className="text-header text-base">{t('passkey.what_are_passkeys_title')}</p>
          <p className="text-meta text-base">{t('passkey.what_are_passkeys_description')}</p>
        </div>
        <div className="border-light flex flex-col py-4 mb-4">
          <p className="text-header text-base">{t('passkey.where_passkeys_saved_title')}</p>
          <p className="text-meta text-base">{t('passkey.where_passkeys_saved_description')}</p>
        </div>
        <Button
          onClick={() => {
            const uuid = uuidv4()
            sendEvent('user_passkeyAdd_click', {
              eventSource: 'egWealth',
              category: 'passkey-add',
              subCategory: 'intent',
              passkeyAttemptUniqueId: uuid,
            })
            void addPasskeyHandler()
          }}
          variant={ButtonVariant.Primary}
        >
          {t('passkey.continue')}
        </Button>
      </ModalDrawer>
    </>
  )
}
